import { captureException, withScope } from "@sentry/react"
import { Component, type ReactNode } from "react"

import { ErrorFallback } from "./error-fallback-screen"

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  componentStack: string
}

export const ErrorBoundary = ({ children }: Props) => (
  <CustomErrorBoundary>{children}</CustomErrorBoundary>
)

class CustomErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, componentStack: "" }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(_error: Error, errorInfo: { componentStack: string }) {
    this.setState({ componentStack: errorInfo.componentStack })
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, componentStack: "" })
  }

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || String(this.state.error)

      if (
        errorMessage.includes("dynamically imported module") ||
        errorMessage.includes("Failed to fetch dynamically imported module") ||
        errorMessage.includes("Failed to load module script") ||
        errorMessage.includes("MIME type") ||
        errorMessage.includes("ERR_BLOCKED_BY_ADBLOCKER")
      ) {
        window.location.reload()
        return null
      }

      // Only send to Sentry when ErrorFallbackScreen is actually rendered
      try {
        withScope((scope) => {
          scope.setLevel("error")
          scope.setExtra("componentStack", this.state.componentStack)
          scope.setTag("error_boundary", "true")
          captureException(this.state.error)
        })
      } catch (_sentryError) {
        // Failed to send error to Sentry
      }

      return (
        <ErrorFallback
          componentStack={this.state.componentStack}
          error={this.state.error}
          resetError={this.resetError}
        />
      )
    }

    return this.props.children
  }
}
